import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import jsPDF from "jspdf";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AnalysisInputModal from "@/components/photos/AnalysisInputModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  MapPin,
  Clock,
  Camera,
  User,
  Building2,
  Calendar,
  Download,
  X,
  CheckCircle,
  AlertCircle,
  Trash2,
  Brain,
  Loader2,
  Zap,
  Flame,
  Droplets,
  Wind,
  Home,
  Plug,
  WrenchIcon,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";

export default function PhotoDetailModal({ 
  photo, 
  jobs, 
  onClose, 
  onAssign, 
  isAssigning, 
  onDelete, 
  isDeleting,
  onAnalyze,
  isAnalyzing 
}) {
  const [selectedJobId, setSelectedJobId] = useState(photo?.job_id || "");
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [sendEmailTo, setSendEmailTo] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const { user } = useAuth();

  // Update selectedJobId when photo changes
  useEffect(() => {
    setSelectedJobId(photo?.job_id || "");
  }, [photo?.id, photo?.job_id]);

  // Reset zoom when photo changes
  useEffect(() => {
    setIsImageZoomed(false);
    setZoomLevel(1);
  }, [photo?.id]);

  // Fetch defects for this photo
  const { data: defects = [], isLoading: loadingDefects, refetch: refetchDefects } = useQuery({
    queryKey: ["photo-defects", photo?.id],
    queryFn: async () => {
      if (!photo?.id) return [];
      const { data, error } = await supabase
        .from("photo_defects")
        .select("*")
        .eq("photo_id", photo.id)
        .order("severity", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!photo?.id,
  });

  // Refetch defects when analysis completes
  useEffect(() => {
    if (photo?.analysis_status === "completed") {
      refetchDefects();
    }
  }, [photo?.analysis_status, refetchDefects]);

  // Fetch inspector (image uploader) email for default recipient
  const { data: inspectorProfile } = useQuery({
    queryKey: ["inspector-email", photo?.inspector_id],
    queryFn: async () => {
      if (!photo?.inspector_id) return null;
      const { data } = await supabase
        .from("profiles_with_email")
        .select("email")
        .eq("id", photo.inspector_id)
        .single();
      return data;
    },
    enabled: !!photo?.inspector_id,
  });

  // Set default email when send modal opens
  useEffect(() => {
    if (isSendEmailModalOpen) {
      setSendEmailTo(inspectorProfile?.email || "");
    }
  }, [isSendEmailModalOpen, inspectorProfile?.email]);

  if (!photo) return null;

  const getCategoryIcon = (category) => {
    switch (category) {
      case "structural": return Home;
      case "electrical": return Plug;
      case "hvac": return Wind;
      case "plumbing": return Droplets;
      case "roofing": return Home;
      case "appliances": return WrenchIcon;
      default: return AlertTriangle;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "structural": return "text-orange-600 bg-orange-100";
      case "electrical": return "text-yellow-600 bg-yellow-100";
      case "hvac": return "text-sky-600 bg-sky-100";
      case "plumbing": return "text-blue-600 bg-blue-100";
      case "roofing": return "text-slate-600 bg-slate-100";
      case "appliances": return "text-purple-600 bg-purple-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "major": return { label: "Major", color: "bg-rose-100 text-rose-700 border-rose-200" };
      case "moderate": return { label: "Moderate", color: "bg-amber-100 text-amber-700 border-amber-200" };
      case "minor": return { label: "Minor", color: "bg-emerald-100 text-emerald-700 border-emerald-200" };
      default: return { label: severity, color: "bg-slate-100 text-slate-700 border-slate-200" };
    }
  };

  const getAnalysisStatusBadge = (status) => {
    switch (status) {
      case "completed": return { label: "Analyzed", color: "bg-violet-100 text-violet-700", icon: CheckCircle };
      case "analyzing": return { label: "Analyzing...", color: "bg-violet-100 text-violet-700", icon: Loader2 };
      case "failed": return { label: "Analysis Failed", color: "bg-rose-100 text-rose-700", icon: AlertCircle };
      default: return { label: "Not Analyzed", color: "bg-slate-100 text-slate-500", icon: Brain };
    }
  };

  const handleAssign = () => {
    if (selectedJobId && onAssign) {
      onAssign(photo.id, selectedJobId);
    }
  };

  const handleOpenAnalysis = () => {
    setIsContextModalOpen(true);
  };

  const handleSubmitAnalysis = (userContext) => {
    onAnalyze?.(photo.id, userContext);
  };

  const loadImageAsDataUrl = async (imageUrl) => {
    if (!imageUrl) return null;
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error("Could not load photo image");
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Could not convert image"));
      reader.readAsDataURL(blob);
    });
  };

  const getImageDimensions = async (dataUrl) =>
    await new Promise((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => resolve({ width: image.width, height: image.height });
      image.onerror = () => reject(new Error("Could not read image dimensions"));
      image.src = dataUrl;
    });

  const generateReportPdfDoc = async () => {
    const reportJob = (jobs || []).find((job) => job.id === photo.job_id);
    const preparedByName = user?.user_metadata?.full_name || "Report Writer";
    const preparedByEmail = user?.email || "Unknown";
    const preparedByRole = user?.user_metadata?.role || "writer";

    const severityCounts = defects.reduce(
      (acc, defect) => {
        const level = defect.severity || "unknown";
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      },
      { major: 0, moderate: 0, minor: 0, unknown: 0 }
    );

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 44;
    const marginTop = 40;
    const marginBottom = 42;
    const contentWidth = pageWidth - marginX * 2;
    const lineHeight = 15;
    let y = marginTop;

    const ensureSpace = (spaceNeeded = 50) => {
      if (y + spaceNeeded > pageHeight - marginBottom) {
        doc.addPage();
        y = marginTop;
      }
    };

    const addSectionTitle = (title) => {
      ensureSpace(40);
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(1.2);
      doc.line(marginX, y, marginX + contentWidth, y);
      y += 16;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(title, marginX, y);
      y += 10;
    };

    const addKeyValue = (label, value) => {
      const safeValue = value ? String(value) : "N/A";
      const wrapped = doc.splitTextToSize(safeValue, contentWidth - 116);
      ensureSpace(Math.max(18, wrapped.length * lineHeight));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      doc.text(`${label}:`, marginX, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
      doc.text(wrapped, marginX + 116, y);
      y += Math.max(18, wrapped.length * lineHeight);
    };

    const addParagraph = (label, value) => {
      if (!value) return;
      ensureSpace(56);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      doc.text(`${label}:`, marginX, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
      const wrapped = doc.splitTextToSize(String(value), contentWidth);
      ensureSpace(wrapped.length * lineHeight + 8);
      doc.text(wrapped, marginX, y);
      y += wrapped.length * lineHeight + 8;
    };

    const formatDateTime = (dateValue) => {
      if (!dateValue) return "N/A";
      try {
        return format(new Date(dateValue), "PPpp");
      } catch (_error) {
        return String(dateValue);
      }
    };

    const statusLabel =
      photo.assignment_status?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
      "Unassigned";

    doc.setFillColor(30, 64, 175);
    doc.rect(0, 0, pageWidth, 88, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("Inspection Analysis Report", marginX, 42);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("ReportEase - Photo QA and Defect Intelligence", marginX, 60);
    doc.text(`Generated: ${format(new Date(), "PPpp")}`, marginX, 74);
    y = 112;

    addSectionTitle("Executive Summary");
    addKeyValue("Photo ID", photo.id);
    addKeyValue("Analysis Status", photo.analysis_status || "pending");
    addKeyValue("Total Defects", defects.length);
    addKeyValue(
      "Severity Distribution",
      `Major ${severityCounts.major} | Moderate ${severityCounts.moderate} | Minor ${severityCounts.minor}`
    );

    if (photo.file_url) {
      try {
        const imageDataUrl = await loadImageAsDataUrl(photo.file_url);
        if (imageDataUrl) {
          const imgDimensions = await getImageDimensions(imageDataUrl);
          const maxImageWidth = contentWidth;
          const maxImageHeight = 280;
          const scale = Math.min(
            maxImageWidth / imgDimensions.width,
            maxImageHeight / imgDimensions.height
          );
          const renderWidth = imgDimensions.width * scale;
          const renderHeight = imgDimensions.height * scale;

          addSectionTitle("Photo Evidence");
          ensureSpace(renderHeight + 30);
          doc.setDrawColor(203, 213, 225);
          doc.setLineWidth(1);
          doc.rect(marginX, y, contentWidth, renderHeight + 12);
          doc.addImage(
            imageDataUrl,
            "JPEG",
            marginX + (contentWidth - renderWidth) / 2,
            y + 6,
            renderWidth,
            renderHeight
          );
          y += renderHeight + 24;
        }
      } catch (_error) {
        addSectionTitle("Photo Evidence");
        addKeyValue("Image", "Image could not be embedded in PDF (URL access issue).");
      }
    }

    addSectionTitle("Job Details");
    addKeyValue("Job ID", reportJob?.id || photo.job_id || "Unassigned");
    addKeyValue("Property", reportJob?.property_name || "Unknown Property");
    addKeyValue("Job Status", reportJob?.status?.replace(/_/g, " ") || "N/A");
    addKeyValue("Job Type", reportJob?.job_type || "N/A");
    addKeyValue("Scheduled Start", formatDateTime(reportJob?.scheduled_start));
    addKeyValue("Scheduled End", formatDateTime(reportJob?.scheduled_end));
    addKeyValue("Inspector (Assigned to Job)", reportJob?.inspector_name || "N/A");

    addSectionTitle("Photo Details");
    addKeyValue("Inspector (Image Uploader)", photo.inspector_name || "Unknown");
    addKeyValue("Taken At", formatDateTime(photo.taken_at));
    addKeyValue("Uploaded At", formatDateTime(photo.created_at));
    addKeyValue("Assignment", statusLabel);
    addKeyValue(
      "GPS Coordinates",
      photo.gps_lat && photo.gps_lng
        ? `${Number(photo.gps_lat).toFixed(6)}, ${Number(photo.gps_lng).toFixed(6)}`
        : "N/A"
    );
    addKeyValue(
      "Device",
      [photo.device_make, photo.device_model].filter(Boolean).join(" ") || "N/A"
    );
    addKeyValue("Source Path", photo.source_dropbox_path || "N/A");

    addSectionTitle("Report Author");
    addKeyValue("Tester / Writer", preparedByName);
    addKeyValue("Email", preparedByEmail);
    addKeyValue("Role", preparedByRole);

    addSectionTitle("Detected Defects");
    if (defects.length === 0) {
      addKeyValue("Result", "No defects detected for this analyzed photo.");
    } else {
      defects.forEach((defect, index) => {
        ensureSpace(110);
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(marginX, y, contentWidth, 26, 4, 4, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        doc.setTextColor(15, 23, 42);
        doc.text(
          `Defect ${index + 1}: ${(defect.category || "general").toString().toUpperCase()} (${defect.severity || "N/A"})`,
          marginX + 10,
          y + 17
        );
        y += 40;

        addKeyValue("Confidence", defect.confidence_score ? `${Math.round(defect.confidence_score * 100)}%` : "N/A");
        addKeyValue("Location in Image", defect.location_in_image || "Not specified");
        addParagraph("Defect Description", defect.defect_description);
        addParagraph("Visual Evidence", defect.visual_evidence);
        addParagraph("Code References", [defect.irc_code && `IRC ${defect.irc_code}`, defect.nec_code && `NEC ${defect.nec_code}`, defect.ipc_code && `IPC ${defect.ipc_code}`, defect.other_code].filter(Boolean).join(" | "));
        addParagraph("Code Description", defect.code_description || defect.irc_code_description);
        addParagraph("Analysis Reasoning", defect.reasoning);
        addParagraph("Recommended Action", defect.recommended_action);
        y += 4;
      });
    }

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(226, 232, 240);
      doc.line(marginX, pageHeight - 28, pageWidth - marginX, pageHeight - 28);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(`ReportEase | Page ${page} of ${pageCount}`, marginX, pageHeight - 14);
    }

    return doc;
  };

  const handleDownloadReportPdf = async () => {
    if (photo.analysis_status !== "completed") {
      toast.error("Please analyze this photo before generating a report PDF.");
      return;
    }

    setIsGeneratingReport(true);
    try {
      const doc = await generateReportPdfDoc();
      const reportDate = format(new Date(), "yyyyMMdd-HHmm");
      const shortPhotoId = photo.id ? String(photo.id).slice(0, 8) : "photo";
      doc.save(`photo-analysis-report-${shortPhotoId}-${reportDate}.pdf`);
      toast.success("Report PDF downloaded.");
    } catch (error) {
      toast.error(error.message || "Failed to generate report PDF.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleSendReportEmail = async (e) => {
    e.preventDefault();
    if (!sendEmailTo?.trim()) {
      toast.error("Please enter an email address.");
      return;
    }

    setIsSendingEmail(true);
    try {
      const doc = await generateReportPdfDoc();
      const pdfBlob = doc.output("blob");
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          resolve(typeof result === "string" ? result.split(",")[1] || "" : "");
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      const reportDate = format(new Date(), "yyyyMMdd-HHmm");
      const shortPhotoId = photo.id ? String(photo.id).slice(0, 8) : "photo";
      const fileName = `photo-analysis-report-${shortPhotoId}-${reportDate}.pdf`;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-report-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            to: sendEmailTo.trim(),
            pdfBase64,
            fileName,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to send email");

      toast.success("Report sent successfully!");
      setIsSendEmailModalOpen(false);
      setSendEmailTo("");
    } catch (error) {
      toast.error(error.message || "Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "auto_matched":
        return { label: "Auto Matched", color: "bg-emerald-100 text-emerald-700" };
      case "manual_assigned":
        return { label: "Manually Assigned", color: "bg-blue-100 text-blue-700" };
      case "out_of_range":
        return { label: "Out of Range", color: "bg-rose-100 text-rose-700" };
      default:
        return { label: "Unassigned", color: "bg-amber-100 text-amber-700" };
    }
  };

  const status = getStatusBadge(photo.assignment_status);

  return (
    <>
      <Dialog open={!!photo} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[95vh] sm:h-[90vh] overflow-hidden p-0 gap-0">
          {/* Mobile Close Button - Fixed at top right */}
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-2 right-2 z-50 lg:hidden bg-white/90 hover:bg-white shadow-lg rounded-full w-10 h-10"
            onClick={onClose}
          >
            <X className="w-5 h-5 text-slate-900" />
          </Button>

          <div className="flex flex-col lg:flex-row h-full min-h-0">
            {/* Image section */}
            <div className="relative lg:w-2/3 bg-slate-900 flex items-center justify-center h-[40vh] sm:h-[45vh] lg:h-full lg:flex-shrink-0 lg:min-h-0">
              {photo.file_url ? (
                <div className="relative w-full h-full flex items-center justify-center group">
                  <img
                    src={photo.file_url}
                    alt="Inspection photo"
                    className="max-w-full max-h-full object-contain cursor-pointer"
                    onClick={() => setIsImageZoomed(true)}
                  />
                  {/* Zoom hint overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-lg px-3 py-2 flex items-center gap-2">
                      <Maximize2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Click to zoom</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-slate-500 flex flex-col items-center gap-2">
                  <Camera className="w-12 h-12" />
                  <span>No preview available</span>
                </div>
              )}           
            </div>

            {/* Details section - scrollable with visible scrollbar */}
            <div 
              className="lg:w-1/3 h-[55vh] sm:h-[45vh] lg:h-full lg:flex-1 lg:min-h-0 overflow-y-auto flex-shrink-0"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 #f1f5f9'
              }}
            >
            <div className="space-y-6 p-6">
              {/* Status */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`${status.color} border-0`}>{status.label}</Badge>
                {(() => {
                  const analysisStatus = getAnalysisStatusBadge(photo.analysis_status);
                  const StatusIcon = analysisStatus.icon;
                  return (
                    <Badge className={`${analysisStatus.color} border-0`}>
                      <StatusIcon className={`w-3 h-3 mr-1 ${photo.analysis_status === "analyzing" ? "animate-spin" : ""}`} />
                      {analysisStatus.label}
                    </Badge>
                  );
                })()}
              </div>

              {/* EXIF Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                  Photo Details
                </h4>
                
                <div className="space-y-3">
                  {photo.taken_at && (
                    <div className="flex items-center gap-3 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {format(new Date(photo.taken_at), "PPpp")}
                      </span>
                    </div>
                  )}
                  
                  {photo.gps_lat && photo.gps_lng && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {photo.gps_lat.toFixed(6)}, {photo.gps_lng.toFixed(6)}
                      </span>
                    </div>
                  )}

                  {(photo.device_make || photo.device_model) && (
                    <div className="flex items-center gap-3 text-sm">
                      <Camera className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {[photo.device_make, photo.device_model].filter(Boolean).join(" ")}
                      </span>
                    </div>
                  )}

                  {photo.inspector_name && (
                    <div className="flex items-center gap-3 text-sm">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">{photo.inspector_name}</span>
                    </div>
                  )}

                  {photo.match_distance_meters && (
                    <div className="flex items-center gap-3 text-sm">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {photo.match_distance_meters.toFixed(0)}m from property
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Analysis Section */}
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                    AI Defect Analysis
                  </h4>
                  <Button
                    size="sm"
                    onClick={handleOpenAnalysis}
                    disabled={isAnalyzing || photo.analysis_status === "analyzing"}
                    className="gap-2 bg-violet-600 hover:bg-violet-700"
                  >
                    {isAnalyzing || photo.analysis_status === "analyzing" ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-3 h-3" />
                        {photo.analysis_status === "completed" ? "Re-analyze" : "Analyze"}
                      </>
                    )}
                  </Button>
                </div>

                {/* Defects Display */}
                {loadingDefects ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : defects.length > 0 ? (
                  <Accordion type="single" collapsible className="w-full">
                    {defects.map((defect, index) => {
                      const CategoryIcon = getCategoryIcon(defect.category);
                      const categoryColor = getCategoryColor(defect.category);
                      const severity = getSeverityBadge(defect.severity);
                      
                      return (
                        <AccordionItem key={defect.id} value={`defect-${index}`} className="border-slate-200">
                          <AccordionTrigger className="hover:no-underline py-3">
                            <div className="flex items-center gap-2 text-left">
                              <div className={`p-1.5 rounded ${categoryColor}`}>
                                <CategoryIcon className="w-3 h-3" />
                              </div>
                              <span className="text-sm font-medium text-slate-700 capitalize">
                                {defect.category}
                              </span>
                              <Badge className={`${severity.color} border text-xs`}>
                                {severity.label}
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pb-3">
                            <div className="space-y-3 text-sm">
                              <p className="text-slate-700 font-medium">{defect.defect_description}</p>
                              
                              {/* Visual Evidence */}
                              {defect.visual_evidence && (
                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-blue-900 mb-1">Visual Evidence</p>
                                  <p className="text-xs text-blue-800">{defect.visual_evidence}</p>
                                </div>
                              )}
                              
                              {/* Code References */}
                              {(defect.irc_code || defect.nec_code || defect.ipc_code || defect.other_code) && (
                                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                                  <p className="text-xs font-semibold text-slate-700 mb-1">Code References</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {defect.irc_code && (
                                      <span className="font-mono text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded border border-orange-200">
                                        IRC {defect.irc_code}
                                      </span>
                                    )}
                                    {defect.nec_code && (
                                      <span className="font-mono text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded border border-yellow-200">
                                        NEC {defect.nec_code}
                                      </span>
                                    )}
                                    {defect.ipc_code && (
                                      <span className="font-mono text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded border border-blue-200">
                                        IPC {defect.ipc_code}
                                      </span>
                                    )}
                                    {defect.other_code && (
                                      <span className="font-mono text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded border border-purple-200">
                                        {defect.other_code}
                                      </span>
                                    )}
                                  </div>
                                  {(defect.code_description || defect.irc_code_description) && (
                                    <p className="text-xs text-slate-600 mt-1.5">
                                      {defect.code_description || defect.irc_code_description}
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              {/* Reasoning */}
                              {defect.reasoning && (
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-slate-700 mb-1">Analysis</p>
                                  <p className="text-xs text-slate-600">{defect.reasoning}</p>
                                </div>
                              )}
                              
                              {/* Recommended Action */}
                              {defect.recommended_action && (
                                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-emerald-900 mb-1">Recommended Action</p>
                                  <p className="text-xs text-emerald-800">{defect.recommended_action}</p>
                                </div>
                              )}
                              
                              {/* Location & Confidence */}
                              <div className="flex items-center justify-between text-xs text-slate-400 pt-1">
                                <span>Location: {defect.location_in_image || "Not specified"}</span>
                                {defect.confidence_score && (
                                  <span className={`font-medium ${
                                    defect.confidence_score >= 0.8 ? 'text-emerald-600' : 
                                    defect.confidence_score >= 0.6 ? 'text-amber-600' : 
                                    'text-rose-600'
                                  }`}>
                                    Confidence: {Math.round(defect.confidence_score * 100)}%
                                  </span>
                                )}
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                ) : photo.analysis_status === "completed" ? (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No defects found in this photo</p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Brain className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Click "Analyze" to detect defects</p>
                  </div>
                )}
              </div>

              {/* Manual Assignment */}
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                  Assign to Job
                </h4>
                
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        <div className="flex flex-col">
                          <span>{job.property_name || "Unknown Property"}</span>
                          <span className="text-xs text-slate-500">
                            {job.scheduled_start
                              ? format(new Date(job.scheduled_start), "MMM d, yyyy")
                              : "No date"}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  onClick={handleAssign}
                  disabled={!selectedJobId || isAssigning}
                  className="w-full"
                >
                  {isAssigning ? "Assigning..." : "Assign Photo"}
                </Button>
              </div>

              {/* Download */}
              {(photo.file_url || photo.analysis_status === "completed") && (
                <div className="pt-4 border-t border-slate-200">
                  <Button
                    onClick={handleDownloadReportPdf}
                    disabled={isGeneratingReport || loadingDefects || photo.analysis_status !== "completed"}
                    className="w-full gap-2 mb-2 bg-slate-900 hover:bg-slate-800"
                  >
                    {isGeneratingReport ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {isGeneratingReport ? "Generating Report..." : "Download Analysis PDF"}
                  </Button>

                  <Button
                    onClick={() => setIsSendEmailModalOpen(true)}
                    disabled={isGeneratingReport || loadingDefects || photo.analysis_status !== "completed"}
                    variant="outline"
                    className="w-full gap-2 mb-2"
                  >
                    <Mail className="w-4 h-4" />
                    Send via Email
                  </Button>

                  {photo.file_url && (
                    <a
                      href={photo.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <Button variant="outline" className="w-full gap-2">
                        <Download className="w-4 h-4" />
                        Download Original
                      </Button>
                    </a>
                  )}
                  {photo.analysis_status !== "completed" && (
                    <p className="text-xs text-slate-500 mt-2">
                      Run AI analysis first to enable PDF report export.
                    </p>
                  )}
                </div>
              )}

              {/* Delete */}
              <div className="pt-4 border-t border-slate-200">
                <Button
                  variant="destructive"
                  className="w-full gap-2"
                  onClick={() => onDelete?.(photo.id)}
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4" />
                  {isDeleting ? "Deleting..." : "Delete Photo"}
                </Button>
              </div>
            </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Zoom Modal */}
      <Dialog open={isImageZoomed} onOpenChange={setIsImageZoomed}>
        <DialogContent className="max-w-[95vw] w-full h-[95vh] p-0 bg-black/95 overflow-hidden">
          <div 
            className="relative w-full h-full overflow-auto"
            style={{
              display: 'flex',
              alignItems: zoomLevel === 1 ? 'center' : 'flex-start',
              justifyContent: zoomLevel === 1 ? 'center' : 'flex-start',
            }}
          >
            {photo.file_url && (
              <>
                <div className="relative" style={{ 
                  minWidth: '100%',
                  minHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <img
                    src={photo.file_url}
                    alt="Zoomed inspection photo"
                    className="transition-transform duration-200"
                    style={{ 
                      transform: `scale(${zoomLevel})`,
                      maxWidth: zoomLevel === 1 ? '100%' : 'none',
                      maxHeight: zoomLevel === 1 ? '100%' : 'none',
                      objectFit: 'contain',
                      transformOrigin: 'center center',
                    }}
                  />
                </div>
                {/* Zoom controls */}
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-2 z-50">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-white text-sm font-medium min-w-[4rem] text-center">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <div className="w-px h-6 bg-white/20 mx-1" />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => setZoomLevel(1)}
                  >
                    Reset
                  </Button>
                </div>
                {/* Close button */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="fixed top-4 right-4 text-white hover:bg-white/20 z-50"
                  onClick={() => setIsImageZoomed(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AnalysisInputModal
        open={isContextModalOpen}
        onClose={() => setIsContextModalOpen(false)}
        onSubmit={handleSubmitAnalysis}
        isAnalyzing={isAnalyzing || photo.analysis_status === "analyzing"}
      />

      {/* Send Report via Email Modal */}
      <Dialog open={isSendEmailModalOpen} onOpenChange={setIsSendEmailModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Report via Email</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Enter the recipient email address. The analysis report PDF will be attached.
          </p>
          <form onSubmit={handleSendReportEmail} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="send-email-to">Recipient Email</Label>
              <Input
                id="send-email-to"
                type="email"
                placeholder="inspector@example.com"
                value={sendEmailTo}
                onChange={(e) => setSendEmailTo(e.target.value)}
                required
              />
              {inspectorProfile?.email && (
                <p className="text-xs text-slate-500">
                  Default: Image uploader ({inspectorProfile.email})
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isSendingEmail}
            >
              {isSendingEmail ? "Sending..." : "Send Report"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}